// ═══════════════════════════════════════════════════════════════════════
// filterSystem.jsx — Single source of truth for interface filtering
// ═══════════════════════════════════════════════════════════════════════
// Replaces the duplicated, drifting filter logic that previously lived
// inline in TableTab, KanbanTab, RiskMatrixTab, TimelineTab, and
// VisualSelection. All tabs now call useFilteredInterfaces(state) and get
// the same filtered list back.
//
// EXPORTS
//   useFilteredInterfaces(state)  — memoized hook returning filtered IFACES
//   filterInterfaces(state)       — pure selector (use in non-React code)
//   matchesFilters(iface, state)  — single-row predicate
//   FilterPanel                   — collapsible, hierarchical filter sidebar
//
// FILTER KEYS supported (state.filters[key] is a Set of values)
//   grp, mvp, phase, complexity, risk, outcomeCat,
//   triggerType, protocolCat, businessObject, status
//
// ═══════════════════════════════════════════════════════════════════════

import React, { useMemo, useState, useCallback, useEffect } from "react";
import {
  IFACES,
  GRP_META,
  CX_META,
  MVP_META,
  RISK_META,
  PHASE_META,
  TRIGGER_TYPES,
  getInterfaceProgress,
} from "./IfacesData";
import {
  getProtocolsByInterface,
  getProtocolCategorySummary,
} from "./IF_Protocols";
import { T, FONTS } from "./theme";

// ═══════════════════════════════════════════════════════════════════════
// TRIGGER FAMILY GROUPING (for hierarchical filter rendering)
// ═══════════════════════════════════════════════════════════════════════
export const TRIGGER_FAMILIES = {
  Lifecycle: ["CREATE", "UPDATE", "DELETE"],
  Workflow:  ["APPROVE", "REJECT", "RELEASE", "COMPLETE"],
  State:     ["STATE_CHANGE", "ATTRIBUTE_CHANGE"],
  Schedule:  ["SCHEDULE"],
};

const FAMILY_BY_TYPE = (() => {
  const map = {};
  for (const [fam, types] of Object.entries(TRIGGER_FAMILIES)) {
    for (const t of types) map[t] = fam;
  }
  return map;
})();

const STATUS_OPTIONS = [
  { value: "completed",   label: "Completed",   color: T.green,  icon: "✓" },
  { value: "in-progress", label: "In Progress", color: T.amber,  icon: "◐" },
  { value: "not-started", label: "Not Started", color: T.text2,  icon: "○" },
  { value: "nis",         label: "Not in Scope",color: T.red,    icon: "⛔" },
];

// ═══════════════════════════════════════════════════════════════════════
// PURE SELECTORS
// ═══════════════════════════════════════════════════════════════════════

/**
 * matchesFilters — true if iface passes every active filter and search.
 * Defensive against missing filters/search/state keys.
 */
export function matchesFilters(iface, state) {
  // Maturity scope gate — when a profile restricts which interface groups
  // are in scope, anything outside that scope is hidden across the whole app.
  // state.groupScope is an array of in-scope group keys (set by App from the
  // active maturity profile). If absent, no scoping is applied (back-compat).
  if (Array.isArray(state?.groupScope)) {
    if (state.groupScope.length === 0) return false;          // nothing in scope
    if (!state.groupScope.includes(iface.grp)) return false;  // out of scope
  }

  // Search
  const search = (state?.search || "").toLowerCase().trim();
  if (search) {
    const hay = `${iface.id} ${iface.name} ${iface.from} ${iface.to} ${iface.bo} ${iface.outcomeCat || ""}`.toLowerCase();
    if (!hay.includes(search)) return false;
  }

  const filters = state?.filters || {};
  for (const [key, values] of Object.entries(filters)) {
    if (!values || !values.size) continue;

    switch (key) {
      case "grp":
        if (!values.has(iface.grp)) return false;
        break;
      case "mvp":
        if (!values.has(`MVP-${iface.mvp}`)) return false;
        break;
      case "phase":
        if (!values.has(`P${iface.phase}`)) return false;
        break;
      case "complexity":
        if (!values.has(CX_META[iface.complexity]?.l)) return false;
        break;
      case "risk":
        if (!values.has(RISK_META[iface.risk]?.l)) return false;
        break;
      case "outcomeCat":
        if (!values.has(iface.outcomeCat)) return false;
        break;
      case "triggerType": {
        const has = iface.triggers?.some(t => values.has(t.type)) ?? false;
        if (!has) return false;
        break;
      }
      case "protocolCat": {
        const pi = getProtocolsByInterface(iface.id);
        if (!values.has(pi?.category)) return false;
        break;
      }
      case "businessObject":
        if (!values.has(iface.bo)) return false;
        break;
      case "status": {
        const isNIS = !!state?.nisState?.[iface.id];
        const prog = getInterfaceProgress(iface, state?.dodState, state?.nisState);
        let bucket = "not-started";
        if (isNIS) bucket = "nis";
        else if (prog.pct === 100) bucket = "completed";
        else if (prog.pct > 0) bucket = "in-progress";
        if (!values.has(bucket)) return false;
        break;
      }
      default:
        // Unknown filter key — ignore rather than reject
        break;
    }
  }
  return true;
}

/** Pure selector — full filtered IFACES array */
export function filterInterfaces(state) {
  return IFACES.filter(iface => matchesFilters(iface, state));
}

/** React hook — memoized over the inputs that actually affect filtering */
export function useFilteredInterfaces(state) {
  return useMemo(
    () => filterInterfaces(state),
    [state?.search, state?.filters, state?.dodState, state?.nisState, state?.groupScope]
  );
}

/** Total count of active filter values (across all keys) */
export function countActiveFilters(state) {
  return Object.values(state?.filters || {}).reduce(
    (sum, set) => sum + (set?.size || 0), 0
  );
}

// ═══════════════════════════════════════════════════════════════════════
// FILTER PANEL — collapsible, hierarchical, with active-filter pills
// ═══════════════════════════════════════════════════════════════════════

const COLLAPSE_STORAGE_KEY = "clm_filter_panel_collapsed_v1";

const loadCollapsed = () => {
  try {
    const raw = sessionStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : { phase: false, grp: false, mvp: false, status: false,
                                     complexity: true, risk: true, outcomeCat: true,
                                     triggerType: true, protocolCat: true, businessObject: true };
  } catch { return {}; }
};

const saveCollapsed = (c) => {
  try { sessionStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(c)); } catch {}
};

const styles = {
  wrap: {
    width: 250, flexShrink: 0,
    borderRight: `1px solid ${T.border}`,
    background: "rgba(4,13,26,.95)",
    overflowY: "auto",
    display: "flex", flexDirection: "column",
  },
  header: {
    padding: "12px 14px 8px",
    borderBottom: `1px solid ${T.border}`,
    position: "sticky", top: 0, background: T.bg,
    zIndex: 2,
  },
  title: {
    fontFamily: FONTS.header, fontSize: 10,
    fontWeight: 700, color: T.cyan, letterSpacing: 1,
  },
  toolbar: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginTop: 6,
  },
  link: {
    cursor: "pointer", fontSize: 8, color: T.text2,
    textDecoration: "underline", padding: "2px 6px",
    borderRadius: 3,
  },
  pills: {
    display: "flex", flexWrap: "wrap", gap: 4,
    padding: "8px 10px",
    borderBottom: `1px solid ${T.border2}`,
    background: T.bg2,
  },
  pill: (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 6px 2px 8px",
    borderRadius: 12,
    background: `${color}15`,
    border: `1px solid ${color}40`,
    color, fontSize: 8,
    fontFamily: FONTS.mono,
    cursor: "pointer",
  }),
  group: {
    borderBottom: `1px solid ${T.border2}`,
  },
  groupHeader: (open) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px",
    cursor: "pointer", userSelect: "none",
    background: open ? "rgba(0,229,255,.04)" : "transparent",
    transition: "background .15s ease",
  }),
  groupTitle: {
    fontSize: 8, letterSpacing: 1.5, textTransform: "uppercase",
    color: T.text2, fontWeight: 600,
    display: "flex", alignItems: "center", gap: 6,
  },
  chevron: (open) => ({
    fontSize: 10, color: T.text2,
    transform: open ? "rotate(90deg)" : "none",
    transition: "transform .15s ease",
    display: "inline-block", width: 10,
  }),
  countBadge: (color) => ({
    fontSize: 7, padding: "1px 5px", borderRadius: 8,
    background: `${color}25`, color,
    fontFamily: FONTS.mono,
  }),
  groupBody: { padding: "4px 8px 10px" },
  subFamily: {
    fontSize: 7, color: T.text3, textTransform: "uppercase",
    letterSpacing: 1, padding: "6px 4px 2px",
  },
  option: (active, color) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "5px 6px", cursor: "pointer",
    opacity: active ? 1 : 0.75,
    borderRadius: 4,
    background: active ? `${color}10` : "transparent",
    transition: "background .12s ease",
  }),
  cb: (active, color) => ({
    width: 12, height: 12, borderRadius: 3,
    border: `1.5px solid ${color}`,
    background: active ? color : "transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  }),
  cbCheck: { color: "#000", fontSize: 8, fontWeight: 700 },
  optLabel: (active) => ({
    fontSize: 9.5, color: active ? T.text : T.text2,
    flex: 1, marginLeft: 8,
  }),
  optCount: {
    fontSize: 7, color: T.text3,
    background: T.bg3,
    padding: "0 5px", borderRadius: 8,
  },
};

/** Compute count of interfaces matching a single (key, value) facet —
 *  ignoring all other active filters (so counts reflect "this facet alone"). */
function singleFacetCount(key, value, state) {
  return IFACES.filter(iface => {
    // Run a stripped match using only this single facet
    const fakeState = { filters: { [key]: new Set([value]) } };
    if (!matchesFilters(iface, fakeState)) return false;
    return true;
  }).length;
}

export function FilterPanel({ state, dispatch }) {
  const [collapsed, setCollapsed] = useState(loadCollapsed);

  useEffect(() => { saveCollapsed(collapsed); }, [collapsed]);

  const toggleGroup = useCallback((key) => {
    setCollapsed(c => ({ ...c, [key]: !c[key] }));
  }, []);

  const expandAll = () => {
    const next = {};
    GROUPS.forEach(g => { next[g.key] = false; });
    setCollapsed(next);
  };
  const collapseAll = () => {
    const next = {};
    GROUPS.forEach(g => { next[g.key] = true; });
    setCollapsed(next);
  };

  const totalActive = countActiveFilters(state);
  const activePills = buildActivePills(state, dispatch);

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={styles.title}>🔍 FILTERS {totalActive > 0 && `(${totalActive})`}</span>
          {totalActive > 0 && (
            <span
              style={{ ...styles.link, color: T.red }}
              onClick={() => dispatch({ type: "CLEAR_ALL_FILTERS" })}
            >Clear all</span>
          )}
        </div>
        <div style={styles.toolbar}>
          <span style={styles.link} onClick={expandAll}>Expand all</span>
          <span style={styles.link} onClick={collapseAll}>Collapse all</span>
        </div>
      </div>

      {/* Active filter pill bar */}
      {activePills.length > 0 && (
        <div style={styles.pills}>
          {activePills.map(p => (
            <span key={p.key + "::" + p.value}
                  style={styles.pill(p.color)}
                  title="Click to remove"
                  onClick={p.onRemove}>
              {p.icon && <span style={{ fontSize: 9 }}>{p.icon}</span>}
              <span>{p.label}</span>
              <span style={{ opacity: 0.7, marginLeft: 2 }}>✕</span>
            </span>
          ))}
        </div>
      )}

      {/* Filter groups */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {GROUPS.map(group => (
          <FilterGroup
            key={group.key}
            group={group}
            state={state}
            dispatch={dispatch}
            open={!collapsed[group.key]}
            onToggle={() => toggleGroup(group.key)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Active-pills builder ─────────────────────────────────────────────
function buildActivePills(state, dispatch) {
  const pills = [];
  const filters = state?.filters || {};
  for (const [key, set] of Object.entries(filters)) {
    if (!set || !set.size) continue;
    for (const value of set) {
      const meta = labelAndColorFor(key, value);
      pills.push({
        key, value,
        label: meta.label,
        color: meta.color,
        icon: meta.icon,
        onRemove: () => dispatch({ type: "SET_FILTER", key, value }),
      });
    }
  }
  return pills;
}

function labelAndColorFor(key, value) {
  switch (key) {
    case "grp":            return { label: value, color: GRP_META[value]?.color || T.steel };
    case "mvp":            return { label: value, color: MVP_META[parseInt(value.split("-")[1])]?.c || T.cyan };
    case "phase": {
      const n = parseInt(value.replace("P", ""));
      return { label: `${value} ${PHASE_META[n]?.l || ""}`.trim(), color: PHASE_META[n]?.c || T.cyan };
    }
    case "complexity":     return { label: value, color: levelColor(CX_META, value) };
    case "risk":           return { label: value, color: levelColor(RISK_META, value) };
    case "outcomeCat":     return { label: value, color: T.steel };
    case "triggerType":    return { label: TRIGGER_TYPES[value]?.label || value,
                                    color: TRIGGER_TYPES[value]?.color || T.steel,
                                    icon: TRIGGER_TYPES[value]?.icon };
    case "protocolCat":    return { label: value, color: T.purple };
    case "businessObject": return { label: value, color: T.purple };
    case "status": {
      const opt = STATUS_OPTIONS.find(s => s.value === value);
      return { label: opt?.label || value, color: opt?.color || T.steel, icon: opt?.icon };
    }
    default:               return { label: String(value), color: T.steel };
  }
}

function levelColor(meta, label) {
  for (const v of Object.values(meta || {})) {
    if (v?.l === label) return v.c;
  }
  return T.steel;
}

// ─── Group renderer ───────────────────────────────────────────────────
function FilterGroup({ group, state, dispatch, open, onToggle }) {
  const activeSet = state?.filters?.[group.key] || new Set();
  const options = group.options();
  const hasActive = activeSet.size > 0;

  return (
    <div style={styles.group}>
      <div style={styles.groupHeader(open)} onClick={onToggle}>
        <span style={styles.groupTitle}>
          <span style={styles.chevron(open)}>▸</span>
          {group.label}
        </span>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {hasActive && (
            <span style={styles.countBadge(T.cyan)}>{activeSet.size}</span>
          )}
          {hasActive && (
            <span
              style={styles.link}
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: "CLEAR_FILTER", key: group.key });
              }}
            >clear</span>
          )}
        </div>
      </div>

      {open && (
        <div style={styles.groupBody}>
          {group.render
            ? group.render({ options, activeSet, dispatch, state })
            : <FlatOptions options={options} activeSet={activeSet} dispatch={dispatch}
                           groupKey={group.key} state={state} />
          }
        </div>
      )}
    </div>
  );
}

function FlatOptions({ options, activeSet, dispatch, groupKey, state }) {
  return options.map(opt => (
    <FilterOption
      key={opt.value}
      opt={opt}
      active={activeSet.has(opt.value)}
      groupKey={groupKey}
      state={state}
      dispatch={dispatch}
    />
  ));
}

function FilterOption({ opt, active, groupKey, state, dispatch }) {
  const count = useMemo(
    () => singleFacetCount(groupKey, opt.value, state),
    [groupKey, opt.value]
  );
  // Hide zero-count rows that aren't currently selected
  if (count === 0 && !active) return null;
  return (
    <div
      style={styles.option(active, opt.color)}
      onClick={() => dispatch({ type: "SET_FILTER", key: groupKey, value: opt.value })}
    >
      <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
        <div style={styles.cb(active, opt.color)}>
          {active && <span style={styles.cbCheck}>✓</span>}
        </div>
        {opt.icon && <span style={{ fontSize: 11, marginLeft: 6 }}>{opt.icon}</span>}
        <span style={styles.optLabel(active)} title={opt.label}>
          {opt.label.length > 22 ? opt.label.substring(0, 22) + "…" : opt.label}
        </span>
      </div>
      <span style={styles.optCount}>{count}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// GROUP DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════
const GROUPS = [
  {
    key: "phase",
    label: "Phase",
    options: () =>
      [...new Set(IFACES.map(f => f.phase))].sort((a, b) => a - b).map(p => ({
        value: `P${p}`,
        label: `P${p} ${PHASE_META[p]?.l || ""}`,
        color: PHASE_META[p]?.c || T.cyan,
      })),
  },
  {
    key: "grp",
    label: "Group",
    options: () =>
      Object.entries(GRP_META).map(([k, v]) => ({
        value: k, label: v.label || k, color: v.color || T.steel,
      })),
  },
  {
    key: "mvp",
    label: "MVP",
    options: () =>
      [1, 2, 3].map(m => ({
        value: `MVP-${m}`,
        label: `MVP-${m}`,
        color: MVP_META[m]?.c || T.cyan,
      })),
  },
  {
    key: "status",
    label: "Status",
    options: () => STATUS_OPTIONS.map(s => ({
      value: s.value, label: s.label, color: s.color, icon: s.icon,
    })),
  },
  {
    key: "complexity",
    label: "Complexity",
    options: () =>
      [1, 2, 3, 4].map(c => ({
        value: CX_META[c].l, label: CX_META[c].l, color: CX_META[c].c,
      })),
  },
  {
    key: "risk",
    label: "Risk",
    options: () =>
      [4, 3, 2, 1].map(r => ({
        value: RISK_META[r].l, label: RISK_META[r].l, color: RISK_META[r].c,
      })),
  },
  {
    key: "outcomeCat",
    label: "Outcome",
    options: () =>
      [...new Set(IFACES.map(f => f.outcomeCat).filter(Boolean))]
        .sort()
        .map(o => ({ value: o, label: o, color: T.steel })),
  },
  {
    key: "triggerType",
    label: "Trigger Type",
    options: () =>
      Object.keys(TRIGGER_TYPES).map(t => ({
        value: t,
        label: TRIGGER_TYPES[t].label,
        color: TRIGGER_TYPES[t].color,
        icon: TRIGGER_TYPES[t].icon,
      })),
    /** Custom render: group by Lifecycle / Workflow / State / Schedule families */
    render: ({ options, activeSet, dispatch, state }) => {
      const byFamily = {};
      for (const opt of options) {
        const fam = FAMILY_BY_TYPE[opt.value] || "Other";
        (byFamily[fam] = byFamily[fam] || []).push(opt);
      }
      return Object.keys(TRIGGER_FAMILIES).concat(["Other"]).map(fam => {
        const opts = byFamily[fam];
        if (!opts || opts.length === 0) return null;
        const familyActive = opts.filter(o => activeSet.has(o.value)).length;
        return (
          <div key={fam}>
            <div style={styles.subFamily}>
              {fam} {familyActive > 0 && (
                <span style={{ color: T.cyan }}>· {familyActive} on</span>
              )}
            </div>
            {opts.map(opt => (
              <FilterOption
                key={opt.value}
                opt={opt}
                active={activeSet.has(opt.value)}
                groupKey="triggerType"
                state={state}
                dispatch={dispatch}
              />
            ))}
          </div>
        );
      });
    },
  },
  {
    key: "protocolCat",
    label: "Protocol Category",
    options: () => {
      try {
        return getProtocolCategorySummary().map(cat => ({
          value: cat.id, label: cat.label || cat.id,
          color: T.purple,
        }));
      } catch { return []; }
    },
  },
  {
    key: "businessObject",
    label: "Business Object",
    options: () =>
      [...new Set(IFACES.map(f => f.bo).filter(Boolean))]
        .sort()
        .map(bo => ({ value: bo, label: bo, color: T.purple })),
  },
];

// Default export so consumers can import { FilterPanel, useFilteredInterfaces } from "./filterSystem"
export default FilterPanel;
